<template>
  <div>
    <template
      v-if="
        model.send_type === campaignSendTypeEnum.SEND_TYPE_EXECUTE &&
          model.trigger === campaignTriggerTypes.TRIGGER_NONE
      "
    >
      <el-alert
        :title="
          $t('CampaignForm_Schedule_ScheduleSelector_OptimoveExecuted_Title')
        "
        type="info"
        :description="
          $t('CampaignForm_Schedule_ScheduleSelector_OptimoveExecuted_Desc')
        "
        show-icon
        :closable="false"
      ></el-alert>
    </template>
    <form-section v-else>
      <form-item prop="send_type">
        <radio-group v-model="model.send_type" inline>
          <radio
            :border="true"
            :label="0"
            :title="$t('CampaignForm_Schedule_ScheduleSelector_SendNow_Title')"
            :description="
              $t('CampaignForm_Schedule_ScheduleSelector_SendNow_Desc')
            "
            data-qa="schedule-send-now-option-radio"
          />
          <radio
            :border="true"
            :label="1"
            :title="
              $t('CampaignForm_Schedule_ScheduleSelector_SendLater_Title')
            "
            :description="
              $t('CampaignForm_Schedule_ScheduleSelector_SendLater_Desc')
            "
            data-qa="schedule-send-later-option-radio"
          />
          <radio
            :border="true"
            :label="2"
            :title="$t('CampaignForm_Schedule_ScheduleSelector_Repeat_Title')"
            :description="
              $t('CampaignForm_Schedule_ScheduleSelector_Repeat_Desc')
            "
            data-qa="schedule-repeat-option-radio"
          />
        </radio-group>
      </form-item>
      <div v-if="model.send_type" class="separator"></div>
      <send-later-component v-if="model.send_type === 1" :model="model" />
      <repeat-component v-if="model.send_type === 2" :model="model" />
    </form-section>
    <offline-users-component v-if="showOfflineUsers" :model="model" />
    <persist-content-component
      v-if="model.inbox && !isWorkflowCampaign"
      :model="model"
    />

    <el-alert
      v-if="
        isLegacyThrottling &&
          !isWorkflowCampaign &&
          (!isEditing || isDraft || isPast || isFuture || isLive)
      "
      type="warning"
      :closable="false"
      show-icon
      :title="$t('CampaignForm_Schedule_Campaign_Throttling_Deprecated_Title')"
      class="m-top-10 m-bottom-10"
      data-qa="campaign-throttling-deprecated"
    >
      <div>
        {{
          $t('CampaignForm_Schedule_Campaign_Throttling_Deprecated_Description')
        }}
      </div>
      <el-button
        v-if="!isLive"
        type="primary"
        size="small"
        class="m-top-10"
        data-qa="campaign-throttling-convert"
        @click="convertThrottlingToRateLimit"
      >
        {{ $t('CampaignForm_Schedule_Campaign_Throttling_Convert') }}
      </el-button>
    </el-alert>

    <el-alert
      v-if="
        isLegacyRateLimit &&
          !isWorkflowCampaign &&
          (!isEditing || isDraft || isPast || isFuture || isLive)
      "
      type="warning"
      :closable="false"
      show-icon
      :title="$t('CampaignForm_Schedule_Campaign_RateLimit_Disabled_Title')"
      class="m-top-10 m-bottom-10"
      data-qa="campaign-rate-limit-disabled"
    >
      <div>
        {{
          $t('CampaignForm_Schedule_Campaign_RateLimit_Disabled_Description')
        }}
      </div>
      <el-button
        v-if="!isLive"
        type="primary"
        size="small"
        class="m-top-10"
        data-qa="campaign-rate-limit-convert"
        @click="convertRateLimitToThrottling"
      >
        {{ $t('CampaignForm_Schedule_Campaign_RateLimit_Convert') }}
      </el-button>
    </el-alert>

    <campaign-throttling-component
      v-if="
        showThrottling &&
          !isWorkflowCampaign &&
          (!isEditing || isDraft || isPast || isFuture || isLive)
      "
      :is-throttling-available="isThrottlingAvailable"
      :readonly="isLive || isLegacyThrottling"
      :model="model"
    ></campaign-throttling-component>

    <campaign-rate-limit-component
      v-if="
        showRateLimit &&
          !isWorkflowCampaign &&
          (!isEditing || isDraft || isPast || isFuture || isLive)
      "
      :is-rate-limit-available="isThrottlingAvailable"
      :readonly="isLive || isLegacyRateLimit"
      :model="model"
    ></campaign-rate-limit-component>
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import SendLaterComponent from '@/modules/campaign-manage/components/schedule/SendLaterComponent'
import OfflineUsersComponent from '@/modules/campaign-manage/components/schedule/OfflineUsersComponent'
import RepeatComponent from '@/modules/campaign-manage/components/schedule/RepeatComponent'
import PersistContentComponent from '@/modules/campaign-manage/components/schedule/PersistContentComponent'
import CampaignThrottlingComponent from '@/modules/campaign-manage/components/schedule/CampaignThrottlingComponent'
import CampaignRateLimitComponent from '@/modules/campaign-manage/components/schedule/CampaignRateLimitComponent'
import { campaignThrottlingMode } from '@/modules/project/enum/CampaignThrottlingModeEnum'
import {
  rateLimitMessageTypes,
  channelByMessageType
} from '@/modules/campaign-manage/enum/RateLimitEnum'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignSendTypeEnum } from '@/modules/campaigns/enum/CampaignSendTypeEnums'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'ScheduleSelector',
  components: {
    PersistContentComponent,
    OfflineUsersComponent,
    SendLaterComponent,
    RepeatComponent,
    Radio,
    RadioGroup,
    FormItem,
    FormSection,
    CampaignThrottlingComponent,
    CampaignRateLimitComponent
  },
  data() {
    return {
      campaignSendTypeEnum,
      campaignTriggerTypes
    }
  },
  props: {
    model: {
      type: Object
    }
  },
  computed: {
    isStandardCampaign() {
      return this.model.type === campaignTypeEnum.TYPE_NOTIFICATION
    },
    isWorkflowCampaign() {
      return this.model.type === campaignTypeEnum.TYPE_WORKFLOW
    },
    isEditing() {
      return this.model.id !== null
    },
    isThrottlingAvailable() {
      let isAvailable =
        $hasFeature('featureCampaignThrottling') &&
        [0, 1].includes(this.model.send_type)
      let isOneChannelSelected = false
      if (isAvailable) {
        let possibleChannels = [
          'web_push',
          'sms',
          'email',
          'ios_push',
          'android_push',
          'inbox',
          'webhook'
        ]
        possibleChannels.forEach(channel => {
          isOneChannelSelected = isOneChannelSelected || !!this.model[channel]
        })
      }

      return isAvailable && isOneChannelSelected
    },
    isLegacyThrottling() {
      // campaigns using the deprecated throttling keep it until converted
      return (
        $hasFeature('featureCampaignThrottling') ===
          campaignThrottlingMode.RATE_LIMIT && !!this.model.throttling
      )
    },
    isLegacyRateLimit() {
      // campaigns using the rate limit keep it until converted
      return (
        $hasFeature('featureCampaignThrottling') ===
          campaignThrottlingMode.DELIVERY_WINDOW && !!this.model.rate_limit
      )
    },
    // with throttling disabled both are rendered so they reset their fields
    showThrottling() {
      return (
        ($hasFeature('featureCampaignThrottling') !==
          campaignThrottlingMode.RATE_LIMIT ||
          this.isLegacyThrottling) &&
        !this.isLegacyRateLimit
      )
    },
    showRateLimit() {
      return (
        ($hasFeature('featureCampaignThrottling') !==
          campaignThrottlingMode.DELIVERY_WINDOW ||
          this.isLegacyRateLimit) &&
        !this.isLegacyThrottling
      )
    },
    isDraft() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'draft'
      )
    },
    isPast() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'past'
      )
    },
    isFuture() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'scheduled'
      )
    },
    isLive() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'live'
      )
    },
    showOfflineUsers() {
      if (this.isStandardCampaign) {
        return (
          this.model.ios_push ||
          this.model.android_push ||
          this.model.web_push ||
          this.model.inapp_ios ||
          this.model.inapp_android ||
          this.model.onsite
        )
      } else {
        return false
      }
    }
  },
  methods: {
    convertThrottlingToRateLimit() {
      // spread the hourly limit over the minutes of the hour on every selected channel,
      // the delivery window has no rate limit equivalent and is dropped
      const recipientsPerHour =
        this.model.throttling_data &&
        this.model.throttling_data.recipients_per_hour
      const rateLimitData = {}
      if (recipientsPerHour) {
        rateLimitMessageTypes
          .filter(
            messageType => !!this.model[channelByMessageType[messageType]]
          )
          .forEach(messageType => {
            rateLimitData[messageType] = Math.ceil(recipientsPerHour / 60)
          })
      }

      this.$set(
        this.model,
        'rate_limit',
        Object.keys(rateLimitData).length ? 1 : 0
      )
      this.$set(this.model, 'rate_limit_data', rateLimitData)
      this.model.throttling_data = null
      this.model.throttling = 0
    },
    convertRateLimitToThrottling() {
      // recipients per hour applies to the whole campaign, so the lowest per minute limit is used,
      // with a delivery window of any time on every day
      const limits = Object.values(this.model.rate_limit_data || {})
      if (limits.length) {
        this.model.throttling_data = {
          delivery_window: {
            send_days: [1, 2, 3, 4, 5, 6, 7],
            start_hour: null,
            end_hour: null,
            timezone: this.model.project.timezone
          },
          recipients_per_hour: Math.min(...limits) * 60
        }
        this.model.throttling = 1
      }

      this.model.rate_limit_data = null
      this.model.rate_limit = 0
    }
  }
}
</script>

<style scoped>
>>> .el-radio {
  height: unset !important;
  min-height: 80px !important;
  max-height: 120px !important;
  white-space: unset;
}

>>> .title {
  font-weight: 600;
  line-height: 22px;
  color: #5c6374;
}
.separator {
  margin: 24px -20px;
  border-top: 1px solid #ebeef5;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/schedule/ScheduleSelector.vue